<div class="bg-white rounded-lg shadow-md px-6 py-5" data-cy="dashboard-due-treatments">

  <!-- Kopf: Titel, Untertitel, Umschalter Liste | Kalender -->
  <div class="flex flex-wrap items-center gap-4">
    <h2 class="text-xl font-bold m-0">{{ 'dashboard.medical.headline' | transloco }}</h2>
    <span class="text-sm text-gray-500">
      {{ 'dashboard.medical.subtitle' | transloco }}
      @if (shelterName) { <span>· {{ shelterName }}</span> }
    </span>
    <div class="ml-auto flex border border-gray-300 rounded-md overflow-hidden">
      <button
        type="button"
        class="flex items-center gap-2 px-4 py-2 text-sm font-semibold cursor-pointer border-0 transition-colors"
        [ngClass]="view === 'list' ? 'bg-[var(--p-primary-500)] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
        [attr.aria-pressed]="view === 'list'"
        (click)="showList()"
      >
        <i class="pi pi-list"></i>{{ 'dashboard.medical.viewList' | transloco }}
      </button>
      <button
        type="button"
        class="flex items-center gap-2 px-4 py-2 text-sm font-semibold cursor-pointer border-0 transition-colors"
        [ngClass]="view === 'calendar' ? 'bg-[var(--p-primary-500)] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
        [attr.aria-pressed]="view === 'calendar'"
        (click)="showCalendar()"
      >
        <i class="pi pi-calendar"></i>{{ 'dashboard.medical.viewCalendar' | transloco }}
      </button>
    </div>
  </div>

  <!-- KPI-Kacheln, zugleich Statusfilter -->
  <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
    @for (tile of tiles; track tile.key) {
      <button
        type="button"
        class="flex items-center gap-3 border rounded-md px-4 py-3 text-left cursor-pointer transition-colors"
        [ngClass]="[
          tile.active ? 'due-tile-' + tile.key : 'border-gray-200 bg-white opacity-60'
        ]"
        [attr.aria-pressed]="tile.active"
        (click)="toggleStatus(tile.key)"
      >
        <span
          class="w-5 h-5 flex-none rounded flex items-center justify-center text-xs font-bold text-white border"
          [ngClass]="'due-check-' + tile.key"
        >
          @if (tile.active) { <i class="pi pi-check text-[0.6rem]"></i> }
        </span>
        <span class="flex flex-col">
          <span class="text-2xl font-bold leading-tight" [ngClass]="'due-num-' + tile.key">{{ tile.count }}</span>
          <span class="text-sm">{{ statusLabelKey(tile.key) | transloco }}</span>
        </span>
        <span class="ml-auto text-xs text-gray-500 text-right">{{ tileHint(tile.key) }}</span>
      </button>
    }
  </div>

  <!-- Filterzeile -->
  <div class="flex flex-wrap items-center gap-3 mt-4 pb-4 border-b border-gray-200">
    <p-select
      [options]="categoryOptions"
      [(ngModel)]="category"
      optionLabel="name"
      optionValue="value"
      [showClear]="true"
      [placeholder]="'dashboard.medical.filterCategory' | transloco"
      styleClass="min-w-[11rem]"
      (ngModelChange)="onFilterChanged()"
    />
    <p-select
      [options]="speciesOptions"
      [(ngModel)]="species"
      optionLabel="name"
      optionValue="value"
      [showClear]="true"
      [placeholder]="'dashboard.medical.filterSpecies' | transloco"
      styleClass="min-w-[10rem]"
      (ngModelChange)="onFilterChanged()"
    />
    @if (view === 'list') {
      <p-select
        [options]="horizonOptions"
        [(ngModel)]="horizonDays"
        optionLabel="name"
        optionValue="value"
        styleClass="min-w-[14rem]"
        (ngModelChange)="onFilterChanged()"
      />
    }
    <div class="flex items-center gap-2 ml-2">
      <p-toggleswitch inputId="onlyInStock" [ngModel]="onlyInStock" (ngModelChange)="toggleStock()" />
      <label class="text-sm cursor-pointer" for="onlyInStock">{{ 'dashboard.medical.onlyInStock' | transloco }}</label>
    </div>
    <span class="ml-auto text-sm text-gray-500">
      {{ 'dashboard.medical.asOf' | transloco: { date: asOfLabel } }}
    </span>
  </div>

  <!-- ============================ Liste ============================ -->
  @if (view === 'list') {
    @if (loadingList) {
      <div class="flex justify-center items-center py-10"><p-progressSpinner /></div>
    } @else if (!hasVisibleRows) {
      <div class="border border-dashed border-gray-300 rounded-md py-10 text-center text-gray-500 mt-5">
        {{ 'dashboard.medical.listEmpty' | transloco }}
      </div>
    } @else {
      @for (section of sections; track section.key) {
        <div class="mt-5">
          <div class="flex items-center gap-3 mb-2">
            <span class="w-3 h-3 rounded-sm" [ngClass]="'due-dot-' + section.key"></span>
            <h3 class="text-base font-bold m-0">{{ statusLabelKey(section.key) | transloco }}</h3>
            <span class="text-sm text-gray-500">
              {{ 'dashboard.medical.entryCount' | transloco: { count: section.rows.length } }}
            </span>
          </div>

          <p-table [value]="section.rows" dataKey="treatmentId" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '60rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ 'dashboard.medical.columns.animal' | transloco }}</th>
                <th>{{ 'dashboard.medical.columns.category' | transloco }}</th>
                <th>{{ 'dashboard.medical.columns.types' | transloco }}</th>
                <th>{{ 'dashboard.medical.columns.preparation' | transloco }}</th>
                <th>{{ 'dashboard.medical.columns.dueAt' | transloco }}</th>
                <th>{{ 'dashboard.medical.columns.status' | transloco }}</th>
                <th>{{ 'dashboard.medical.columns.veterinarian' | transloco }}</th>
                <th class="text-right">{{ 'dashboard.medical.columns.action' | transloco }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [ngClass]="row.notInStock ? 'bg-gray-50 text-gray-500' : ''">
                <td>
                  <div class="flex items-center gap-3 min-w-0">
                    <span
                      class="w-9 h-9 flex-none rounded-full flex items-center justify-center font-bold text-gray-700"
                      [style.background]="row.avatarColor"
                    >{{ row.initial }}</span>
                    <span class="min-w-0">
                      <span class="flex items-center gap-2">
                        <span class="font-bold truncate">
                          {{ row.animalName || ('dashboard.medical.unnamedAnimal' | transloco) }}
                        </span>
                        @if (row.notInStock) {
                          <span class="bg-gray-100 text-gray-500 rounded px-2 py-0.5 text-xs whitespace-nowrap">
                            {{ 'dashboard.medical.notInStock' | transloco }}
                          </span>
                        }
                      </span>
                      <span class="block text-xs text-gray-500 truncate">
                        {{ row.speciesLabel }}@if (row.bookNumber) { · {{ row.bookNumber }} }
                      </span>
                    </span>
                  </div>
                </td>
                <td class="text-gray-600">{{ row.categoryLabel }}</td>
                <td>
                  <div class="flex flex-wrap gap-1">
                    @for (type of row.typeLabels; track type) {
                      <span class="bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-xs font-semibold">{{ type }}</span>
                    }
                    @if (!row.typeLabels.length) { <span class="text-gray-400">–</span> }
                  </div>
                </td>
                <td>{{ row.preparation || '–' }}</td>
                <td>{{ row.dueAt | date:'dd.MM.yyyy' }}</td>
                <td><p-tag [severity]="statusSeverity(row.status)" [value]="row.daysLabel" /></td>
                <td class="text-gray-600">{{ row.vetName }}</td>
                <td class="text-right">
                  <p-button
                    size="small"
                    [outlined]="true"
                    [label]="'dashboard.medical.record' | transloco"
                    (onClick)="openAnimal(row)"
                  />
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      }

      <p class="text-xs text-gray-500 mt-4 m-0">{{ 'dashboard.medical.listHint' | transloco }}</p>
    }
  }

  <!-- ============================ Kalender ============================ -->
  @if (view === 'calendar') {
    <div class="flex flex-col xl:flex-row gap-6 mt-5 items-start">

      <!-- Links: Monatsraster -->
      <div class="flex-1 min-w-0 w-full">
        <div class="flex flex-wrap items-center gap-3 mb-3">
          <p-button
            size="small"
            [outlined]="true"
            severity="secondary"
            icon="pi pi-chevron-left"
            [ariaLabel]="'dashboard.medical.previousMonth' | transloco"
            (onClick)="shiftMonth(-1)"
          />
          <p-button
            size="small"
            [outlined]="true"
            severity="secondary"
            icon="pi pi-chevron-right"
            [ariaLabel]="'dashboard.medical.nextMonth' | transloco"
            (onClick)="shiftMonth(1)"
          />
          <span class="text-lg font-bold">{{ monthLabel }}</span>
          <p-button
            size="small"
            [outlined]="true"
            severity="secondary"
            [label]="'dashboard.medical.today' | transloco"
            (onClick)="goToCurrentMonth()"
          />
          <div class="ml-auto flex items-center gap-4 text-xs text-gray-600">
            @for (status of statusOrder; track status) {
              <span class="flex items-center gap-1.5">
                <span class="w-2.5 h-2.5 rounded-sm" [ngClass]="'due-dot-' + status"></span>
                {{ statusLabelKey(status) | transloco }}
              </span>
            }
          </div>
        </div>

        @if (loadingCalendar) {
          <div class="flex justify-center items-center py-10"><p-progressSpinner /></div>
        } @else {
          <div class="border border-gray-200 rounded-md overflow-hidden">
            <div class="grid grid-cols-7 bg-[var(--p-primary-50)] text-[var(--p-primary-700)] font-semibold text-sm">
              @for (weekday of weekdays; track weekday) {
                <div class="px-3 py-2">{{ weekday }}</div>
              }
            </div>
            <div class="grid grid-cols-7">
              @for (day of grid; track day.iso) {
                <button
                  type="button"
                  class="due-calendar-day border-t border-l border-gray-100 p-2 flex flex-col gap-1 text-left cursor-pointer transition-colors"
                  [ngClass]="[
                    selectedDay === day.iso ? 'bg-[var(--p-primary-50)] ring-2 ring-inset ring-[var(--p-primary-500)]' : (day.inMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'),
                    day.inMonth ? '' : 'text-gray-400'
                  ]"
                  [attr.aria-pressed]="selectedDay === day.iso"
                  (click)="selectDay(day)"
                >
                  <span
                    class="text-sm font-semibold"
                    [ngClass]="day.isToday
                      ? 'bg-[var(--p-primary-500)] text-white rounded-full min-w-[1.5rem] h-6 inline-flex items-center justify-center'
                      : 'px-1'"
                  >{{ day.date | date:'d' }}</span>

                  @for (entry of entriesFor(day.iso) | slice:0:3; track entry.treatmentId) {
                    <span
                      class="due-calendar-entry text-xs font-semibold rounded px-1.5 py-0.5 w-full"
                      [ngClass]="'due-chip-' + entry.status"
                      [class.opacity-55]="!day.inMonth"
                      [pTooltip]="entry.animalLabel + ' · ' + entry.label"
                      tooltipPosition="top"
                    >{{ entry.animalName || ('dashboard.medical.unnamedAnimal' | transloco) }} · {{ entry.typeLabels[0] || entry.categoryLabel }}@if (entry.typeLabels.length > 1) { +{{ entry.typeLabels.length - 1 }} }</span>
                  }
                  @if (entriesFor(day.iso).length > 3) {
                    <span class="text-xs text-gray-600 font-semibold pl-1">
                      {{ 'dashboard.medical.more' | transloco: { count: entriesFor(day.iso).length - 3 } }}
                    </span>
                  }
                </button>
              }
            </div>
          </div>
        }
      </div>

      <!-- Rechts: Tagesdetail und Überfällig-Box -->
      <div class="w-full xl:w-[360px] xl:flex-none flex flex-col gap-4">
        <div class="border border-gray-200 rounded-md overflow-hidden">
          <div class="bg-[var(--p-primary-50)] px-4 py-3">
            <div class="text-[var(--p-primary-700)] font-bold">{{ selectedDayLabel }}</div>
            <div class="text-xs text-gray-600 mt-0.5">
              @if (selectedEntries.length) {
                {{ 'dashboard.medical.dueCount' | transloco: { count: selectedEntries.length } }}
              } @else {
                {{ 'dashboard.medical.dayEmptyShort' | transloco }}
              }
            </div>
          </div>

          @if (!selectedEntries.length) {
            <div class="px-4 py-6 border-t border-gray-100 text-center text-gray-500 text-sm">
              {{ 'dashboard.medical.dayEmpty' | transloco }}
            </div>
          } @else {
            @for (entry of selectedEntries; track entry.treatmentId) {
              <button
                type="button"
                class="w-full px-4 py-3 border-t border-gray-100 flex flex-col gap-2 text-left cursor-pointer hover:bg-gray-50"
                (click)="openAnimal(entry)"
              >
                <span class="flex items-center gap-3">
                  <span
                    class="w-9 h-9 flex-none rounded-full flex items-center justify-center font-bold text-gray-700"
                    [style.background]="entry.avatarColor"
                  >{{ entry.initial }}</span>
                  <span class="min-w-0 flex-1">
                    <span class="block font-bold truncate">
                      {{ entry.animalName || ('dashboard.medical.unnamedAnimal' | transloco) }}
                    </span>
                    <span class="block text-xs text-gray-500 truncate">
                      {{ entry.speciesLabel }}@if (entry.bookNumber) { · {{ entry.bookNumber }} }
                    </span>
                  </span>
                  <p-tag [severity]="statusSeverity(entry.status)" [value]="entry.daysLabel" />
                </span>
                <span class="flex flex-wrap gap-1 items-center">
                  <span class="text-xs text-gray-600">{{ entry.categoryLabel }}:</span>
                  @for (type of entry.typeLabels; track type) {
                    <span class="bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-xs font-semibold">{{ type }}</span>
                  }
                </span>
                <span class="text-xs text-gray-500 truncate">
                  {{ entry.preparation || '–' }} · {{ entry.vetName }}
                </span>
              </button>
            }
          }
        </div>

        @if (overdueCount) {
          <button
            type="button"
            class="w-full border border-red-200 bg-red-50 rounded-md px-4 py-3 flex items-center gap-3 text-left cursor-pointer hover:bg-red-100 transition-colors"
            (click)="showOverdueList()"
          >
            <span class="text-2xl font-bold text-red-700 leading-none">{{ overdueCount }}</span>
            <span class="text-sm text-red-900">{{ 'dashboard.medical.overdueBox' | transloco }}</span>
            <span class="ml-auto text-sm font-semibold text-red-700 whitespace-nowrap">
              {{ 'dashboard.medical.toList' | transloco }} →
            </span>
          </button>
        }
      </div>
    </div>
  }
</div>
